import { useRef, useCallback, KeyboardEvent, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Badge, Box, Button, Flex, rem, Text } from '@vimeo/bokeh';

import { getHoverStateItems, SHOWCASE_BASE_URL } from '../utils/constants';
import { HoverStateType } from '../utils/types';

interface Props {
  type: HoverStateType;
  isPlaybackPath?: boolean;
  showcaseId?: number;
}

export const HoverState = ({ isPlaybackPath, showcaseId, type }: Props) => {
  const { title, items } = getHoverStateItems(type, isPlaybackPath);
  const history = useHistory();
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const handleClick = (route: string) => {
    if (!showcaseId) return;
    const redirectUrl = `${SHOWCASE_BASE_URL}${showcaseId}${route}`;
    history.replace(redirectUrl);
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = (focusedIndex + 1) % items.length;
        setFocusedIndex(nextIndex);
        buttonRefs.current[nextIndex]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex = (focusedIndex - 1 + items.length) % items.length;
        setFocusedIndex(prevIndex);
        buttonRefs.current[prevIndex]?.focus();
      }
    },
    [focusedIndex, items.length],
  );

  return (
    <Box
      data-component-id={`${type}-hover-wrapper`}
      opacity={0}
      transition="opacity 200ms ease-in-out"
      zIndex={2}
    >
      <Badge
        variant="new"
        size="xs"
        borderRadius={0}
        position="absolute"
        top={rem(-2)}
        left={rem(-2)}
      >
        {title}
      </Badge>
      <Flex
        borderRadius={rem(12)}
        direction="column"
        justify="center"
        align="center"
        p="sm"
        position="absolute"
        right="sm"
        top="sm"
        zIndex={2}
        bg="popover"
        minW={type === 'navigation' ? rem(106) : rem(160)}
        boxShadow="md"
        gap="xs"
        onKeyDown={handleKeyDown}
        role="menu"
        aria-label={title}
      >
        {items.map((item, idx) => (
          <Button
            _focus={{ bg: 'darkBlueAlpha.200' }}
            key={item.route}
            w="100%"
            h={rem(36)}
            px="3"
            borderRadius="sm"
            variant="tertiary"
            bg="popover"
            onClick={() => handleClick(item.route)}
            ref={(el) => {
              buttonRefs.current[idx] = el;
            }}
            role="menuitem"
            tabIndex={idx === focusedIndex ? 0 : -1}
            onFocus={() => setFocusedIndex(idx)}
          >
            <Text w="100%" textAlign="left" variant="body-md">
              {item.label}
            </Text>
          </Button>
        ))}
      </Flex>
    </Box>
  );
};
